import { useCallback, useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { Doctrine } from '../lib/types.js';

/** Manual editor for the Hymn doctrine overlay (`apps/server/data/doctrine.json`) — the hook
 * "The Choir Learns" (daily doctrine automation, M3.5) will read and rewrite once it lands. */
export function Choir() {
  const fetchDoctrine = useCallback(() => api.get<Doctrine>('/api/admin/doctrine'), []);
  const doctrine = useFetch(fetchDoctrine, []);

  const [text, setText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (doctrine.data && !dirty) setText(JSON.stringify(doctrine.data, null, 2));
  }, [doctrine.data, dirty]);

  const save = async () => {
    setSaveError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setSaveError('Not valid JSON.');
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setSaveError('Doctrine must be a JSON object.');
      return;
    }

    setSaving(true);
    try {
      await api.put('/api/admin/doctrine', parsed);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      doctrine.reload();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Failed to save doctrine.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 class="page-title">Choir</h1>
      <div class="hint" style={{ marginBottom: 14 }}>
        The Hymn doctrine overlay — raw JSON the raid AI will read once daily doctrine automation
        ("The Choir Learns") lands in M3.5. This is the manual hook: nothing recomputes it yet, so
        edits here stick until someone changes them again.
      </div>

      {doctrine.error && <div class="error-banner">{doctrine.error}</div>}

      <div class="card">
        <div class="field">
          <label>Doctrine JSON</label>
          <textarea
            class="textarea"
            style={{ width: '100%', minHeight: 360 }}
            value={text}
            disabled={doctrine.loading}
            onInput={(e) => {
              setText((e.target as HTMLTextAreaElement).value);
              setDirty(true);
              setSaved(false);
            }}
          />
        </div>

        {saveError && <div class="error-banner">{saveError}</div>}

        <div class="row" style={{ justifyContent: 'space-between' }}>
          <span class="hint">{dirty ? 'Unsaved changes.' : saved ? 'Saved.' : ' '}</span>
          <button class="btn btn-primary" disabled={saving || doctrine.loading} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
